/**
 * ChatContext.js
 * Global state for internal messaging — API only, no localStorage.
 *
 * Endpoints:
 *   POST  InternalMessageApi                                         — send message
 *   GET   InternalMessageApi/conversation/{cId}/{uId}/{oId}         — get conversation
 *   GET   InternalMessageApi/{id}                                    — get single message
 */
import React, {
  createContext, useContext, useState,
  useEffect, useCallback, useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { fetchData, postData, updateData } from './apiService';

const ChatContext = createContext();

const CTRL    = 'InternalMessageApi';
const POLL_MS = 3000;

// ── map raw API message → internal shape ─────────────────────────
const mapMsg = m => ({
  id:        String(m.Id          ?? m.id          ?? ''),
  fromId:    String(m.FromUserId  ?? m.fromUserId  ?? m.SenderId   ?? m.senderId   ?? ''),
  toId:      String(m.ToUserId    ?? m.toUserId    ?? m.ReceiverId ?? m.receiverId ?? ''),
  text: (() => {
    const hasAtt = m.AttachmentData ?? m.attachmentData;
    if (hasAtt) {
      return JSON.stringify({
        text:       m.MessageText    ?? m.messageText    ?? '',
        attachment: {
          name: m.AttachmentName ?? m.attachmentName ?? '',
          type: m.AttachmentType ?? m.attachmentType ?? '',
          size: m.AttachmentSize ?? m.attachmentSize ?? 0,
          data: m.AttachmentData ?? m.attachmentData ?? '',
        },
      });
    }
    return m.MessageText ?? m.messageText ?? m.Message ?? m.message ?? m.Text ?? m.text ?? '';
  })(),
  timestamp: m.SentAt     ?? m.sentAt     ?? m.CreatedAt  ?? m.createdAt  ?? new Date().toISOString(),
  read:      m.IsRead     ?? m.isRead     ?? false,
});

// ── Provider ─────────────────────────────────────────────────────
export const ChatProvider = ({ children }) => {
  const { companyId, userId, token } = useAuth();
  const myId = String(userId || '');

  const [conversations, setConversations] = useState({});
  const [unreadCounts,  setUnreadCounts]  = useState({});
  const [totalUnread,   setTotalUnread]   = useState(0);

  const openConvRef = useRef(null);
  const pollRef     = useRef(null);
  const onUpdateRef = useRef(null);

  // ── derive unread counts ───────────────────────────────────────
  const recalcUnread = useCallback((convs) => {
    const counts = {};
    Object.entries(convs).forEach(([otherId, msgs]) => {
      if (otherId === openConvRef.current) return;
      const n = msgs.filter(m => m.fromId === otherId && !m.read).length;
      if (n > 0) counts[otherId] = n;
    });
    setUnreadCounts(counts);
    setTotalUnread(Object.values(counts).reduce((s, v) => s + v, 0));
  }, []);

  // ── fetch conversation from API ────────────────────────────────
  const runFetch = useCallback(async (otherId) => {
    if (!companyId || !myId || !token) return;
    try {
      const res = await fetchData(CTRL, `/conversation/${companyId}/${myId}/${otherId}`, token);
      const parsed = typeof res === 'string' ? JSON.parse(res) : res;
      const raw = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.response_data)
          ? parsed.response_data
          : null;
      if (!raw) return;
      const msgs = raw.map(mapMsg);
      onUpdateRef.current?.(msgs);
      setConversations(prev => {
        const updated = { ...prev, [otherId]: msgs };
        recalcUnread(updated);
        return updated;
      });
    } catch { /* ignore poll errors */ }
  }, [companyId, myId, token, recalcUnread]);

  // ── stop polling ──────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    onUpdateRef.current = null;
    openConvRef.current = null;
  }, []);

  // ── start polling a conversation ──────────────────────────────
  const startPolling = useCallback((otherId, onUpdate) => {
    stopPolling();
    openConvRef.current = otherId;
    onUpdateRef.current = onUpdate ?? null;
    runFetch(otherId);
    pollRef.current = setInterval(() => runFetch(otherId), POLL_MS);
  }, [runFetch, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // ── get cached messages for sidebar preview ────────────────────
  const getConversation = useCallback((otherId) => {
    return conversations[otherId] ?? [];
  }, [conversations]);

  // ── mark conversation as read — API + local update ───────────
  // ── mark conversation as read — PUT /InternalMessageApi/{id} for each unread msg ──
  const readConversation = useCallback(async (otherId) => {
    openConvRef.current = otherId;

    // 1. Optimistic local update — unread badge clears immediately
    setConversations(prev => {
      const msgs = (prev[otherId] ?? []).map(m =>
        m.fromId === otherId ? { ...m, read: true } : m,
      );
      const updated = { ...prev, [otherId]: msgs };
      recalcUnread(updated);
      return updated;
    });

    if (!companyId || !myId || !token) return;

    // 2. Fetch the conversation to get full message objects for the PUT body
    try {
      const res = await fetchData(CTRL, `/conversation/${companyId}/${myId}/${otherId}`, token);
      const parsed = typeof res === 'string' ? JSON.parse(res) : res;
      const raw = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.response_data)
          ? parsed.response_data
          : [];

      // 3. PUT each unread message (from otherId → me) with IsRead = true
      const unread = raw.filter(m =>
        (m.FromUserId ?? m.fromUserId) === Number(otherId) &&
        (m.ToUserId   ?? m.toUserId)   === Number(myId)   &&
        !(m.IsRead    ?? m.isRead)
      );

      await Promise.all(unread.map(m =>
        updateData(CTRL, m.Id ?? m.id, {
          ...m,
          IsRead: true,
          ReadAt: new Date().toISOString(),
        }, token).catch(err =>
          console.warn(`[Chat] MarkRead failed for msg ${m.Id ?? m.id}:`, err?.message)
        )
      ));

    } catch (err) {
      console.warn('[Chat] readConversation fetch failed:', err?.message);
    }

    // 4. Re-fetch so local state is in sync with backend
    await runFetch(otherId);
  }, [companyId, myId, token, recalcUnread, runFetch]);

  // ── send a message — API only ─────────────────────────────────
  const sendMessage = useCallback(async (toId, text) => {
    if (!companyId || !myId || !token) return null;
    const msgText = String(text ?? '').trim();
    if (!msgText) return null;

    try {
      let messageText    = msgText;
      let attachmentName = null, attachmentType = null;
      let attachmentSize = null, attachmentData = null;

      try {
        const parsed = JSON.parse(msgText);
        if (parsed?.attachment) {
          messageText    = parsed.text            || null;
          attachmentName = parsed.attachment.name || null;
          attachmentType = parsed.attachment.type || null;
          attachmentSize = parsed.attachment.size || null;
          attachmentData = parsed.attachment.data || null;
        }
      } catch { /* plain text message */ }

      await postData(CTRL, '', {
        CompanyId:      Number(companyId),
        FromUserId:     Number(myId),
        ToUserId:       Number(toId),
        MessageText:    messageText,
        AttachmentName: attachmentName,
        AttachmentType: attachmentType,
        AttachmentSize: attachmentSize ? Number(attachmentSize) : null,
        AttachmentData: attachmentData,
        SentAt:         new Date(),
        IsRead:         false,
        ReadAt:         null,
      }, token);

      // immediately re-fetch so sent message appears without waiting for next poll tick
      await runFetch(toId);
      return true;
    } catch (err) {
      console.warn('[Chat] sendMessage failed:', err?.message);
      return null;
    }
  }, [companyId, myId, token, runFetch]);

  // ── fetch single message by id ────────────────────────────────
  const getMessageById = useCallback(async (id) => {
    if (!token) return null;
    try {
      const res = await fetchData(CTRL, `/${id}`, token);
      const parsed = typeof res === 'string' ? JSON.parse(res) : res;
      const raw = parsed?.response_data ?? parsed;
      return raw ? mapMsg(raw) : null;
    } catch {
      return null;
    }
  }, [token]);

  // ── fetch users list ──────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    if (!companyId || !myId || !token) return [];
    try {
      const res = await fetchData(CTRL, `/users/${companyId}/${myId}`, token);
      const parsed = typeof res === 'string' ? JSON.parse(res) : res;
      const raw = Array.isArray(parsed) ? parsed : (parsed?.response_data ?? []);
      return raw
        .filter(u => String(u.Id ?? u.id) !== myId)
        .map(u => ({
          id:       String(u.Id       ?? u.id),
          name:     u.UserName ?? u.Name ?? `User ${u.Id ?? u.id}`,
          role:     u.RoleName ?? u.Role ?? '',
          isActive: u.IsActive !== false,
        }));
    } catch {
      return [];
    }
  }, [companyId, myId, token]);

  return (
    <ChatContext.Provider value={{
      unreadCounts,
      totalUnread,
      sendMessage,
      getConversation,
      readConversation,
      startPolling,
      stopPolling,
      getMessageById,
      fetchUsers,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};
